import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { CommerceShop } from "../models/commerce-shop.model";
import {
  addDoc,
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs, limit,
  onSnapshot,
  query, Unsubscribe,
  updateDoc,
  where
} from "@angular/fire/firestore";
import { ObjectHelper } from "../helpers/object.helper";
import { Smtp } from "../models/smtp.model";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { PaymentPlatform } from "../models/payment-platform.model";

@Injectable({
  providedIn: 'root',
})
export class ShopService {

  private firestore = inject(Firestore);
  private functions = inject(Functions);

  private shops$: [string, BehaviorSubject<CommerceShop[]>];
  private unsubscribeShops: Unsubscribe = () => {
    return;
  };
  
  async shopsSync(organizationId: string): Promise<CommerceShop[]> {
    const q = query(
      collection(this.firestore, 'shop'),
      where('organizationId', '==', organizationId)
    )
      .withConverter(CommerceShop.converter);
    const snap = await getDocs(q)
    if (snap.empty) {
      return [];
    }
    return snap.docs.map(doc => doc.data())
  }

  /**
   * Get the web shops
   * @param organizationId organizationId
   * @returns The observable
   */
  shops(organizationId: string): BehaviorSubject<CommerceShop[]> {
    if (this.shops$?.[0] !== organizationId) {
      this.unsubscribeShops()
      this.shops$ = [organizationId, new BehaviorSubject<CommerceShop[]>([])];
      const q = query(
        collection(this.firestore, 'shop'),
        where('organizationId', '==', organizationId)
      )
        .withConverter(CommerceShop.converter);
      this.unsubscribeShops = onSnapshot(q, snapshot => {
        if (snapshot.empty) {
          return this.shops$[1].next([]);
        }
        this.shops$[1].next(snapshot.docs.map(doc => doc.data()));
      });
    }
    return this.shops$[1];
  }

  /**
   * Get the shop by id
   * @param id The shop id
   * @returns The shop
   */
  async shop(id: string): Promise<CommerceShop> {
    const ref = doc(this.firestore, 'shop', id)
      .withConverter(CommerceShop.converter);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return null;
    }
    return docSnap.data();
  }

  /**
   * Create a new shop
   * @param shop the shop to create
   * @returns Created shop id
   */
  async addShop(shop: Partial<CommerceShop>): Promise<string> {
    const ref = collection(this.firestore, 'shop')
      .withConverter(CommerceShop.converter);
    return addDoc(ref, ObjectHelper.anonymize(shop))
      .then(ref => ref.id);
  }

  /**
   * Update the shop
   * @param id The shop id
   * @param shop The shop to update
   * @returns void
   */
  updateShop(id: string, shop: Partial<CommerceShop>): Promise<void> {
    const ref = doc(this.firestore, 'shop', id).withConverter(CommerceShop.converter);
    return updateDoc(ref, ObjectHelper.anonymize(shop));
  }

  /**
   * Get the shop smtp settings
   * @param shopId The shop id
   * @returns Shop smtp config
   */
  async shopSmtp(shopId: string): Promise<Smtp> {
    const ref = doc(this.firestore, 'smtp', shopId).withConverter(Smtp.converter);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return new Smtp({ id: shopId });
    }
    return docSnap.data();
  }

  /**
   * Update the shop smtp settings
   * @param smtp The smtp to update
   * @returns Updated Smtp
   */
  async updateSmtp(smtp: Smtp): Promise<Smtp> {
    return httpsCallable<Smtp, Smtp>(this.functions, 'update_smtp_gen2')(smtp)
      .then(result => result.data);
  }

  /**
   * Get the shop payment platforms
   * @param shopId The shop id
   * @returns shops payments platforms
   */
  async shopPaymentPlatforms(shopId: string): Promise<PaymentPlatform[]> {
    const q = query(
      collection(this.firestore, 'payment-platforms'),
      where('shop', '==', shopId)
    ).withConverter(PaymentPlatform.converter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Update the payment platforms
   * @param paymentPlatform The payment platform to update
   * @returns Updated payment platform
   */
  async updatePaymentPlatform(paymentPlatform: PaymentPlatform): Promise<PaymentPlatform> {
    return httpsCallable<PaymentPlatform, PaymentPlatform>(this.functions, 'update_payment_platform_gen2')(paymentPlatform)
      .then(result => result.data);
  }

  /**
   * Send a test email
   * @param email Destination Email to test Smtp config
   * @param lang The language
   * @param shopId The shop id
   * @returns void
   */
  testSmtp(email: string, lang: string, shopId: string): Promise<void> {
    return httpsCallable<{
      email: string,
      lang: string,
      shopId: string
    }, void>(this.functions, 'shop_test_email_gen2')({ email, lang, shopId })
      .then(result => result.data);
  }

  /**
   * Get the shop with the given appKey belonging to the given configuration
   * @param appKey The shop appKey
   * @returns First shop with same appKey
   */
  async shopByAppKey(appKey: string): Promise<CommerceShop> {
    const q = query(
      collection(this.firestore, 'shop'),
      where('appKey', '==', appKey))
      .withConverter(CommerceShop.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return null;
        }
        return snapshot.docs[0].data();
      })
  }

  /**
   * Unsubscribe from shops
   */
  unsubscribe(): void {
    if (this.unsubscribeShops) {
      this.unsubscribeShops();
      this.shops$ = null;
    }
  }

  /**
   * Check app key is available
   * @param appKey the app key to check
   * @returns True if the app key is used
   */
  async getAvailableAppKey(appKey: string): Promise<boolean> {
    const q = query(collection(this.firestore, 'shop'),
      where('appKey', '==', appKey))
      .withConverter(CommerceShop.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty;
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  async getByCustomer(id: string): Promise<CommerceShop | null> {
    const q = query(collection(this.firestore, 'shop'),
      where('customer', '==', id),
      limit(1))
      .withConverter(CommerceShop.converter);
    const snap = await getDocs(q);
    if (snap.empty) {
      return null;
    }
    return snap.docs[0].data();
  }
}
